/* eslint-disable no-console */
import React, { useContext, useEffect } from "react";
import { TextArea } from "@components/Input/TextArea";
import clsx from "clsx";
import styles from "./styles/BasicTab.module.css";
import { SelectField, InputField } from "@components";
import { RichText } from "@components/Input/RichText";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  editingCourseActions,
  getAllCategoriesAction,
  getAllCourseLevelAction,
  getAllAudioLanguageAction,
  updateCourseAction,
  createCourseAction,
} from "@redux/actions";
import { SpinnerMark, TabTitle } from "@components";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { toast } from "react-toastify";
import { CourseContext } from "..";

const BasicTab = () => {
  const { step, setStep, isUpdate, autoSave, setAutoSave } =
    useContext(CourseContext);

  // state
  const {
    editingCourse,
    createCourse,
    updateCourse,
    getDetailsCourseForUpdate,
  } = useSelector((state) => state.course);
  const CourseLevel = useSelector(
    (state) => state.courseLevel.getAllCourseLevel.data
  );
  const AudioLanguage = useSelector(
    (state) => state.audioLanguage.getAllAudioLanguage.data
  );
  const CourseCategory = useSelector(
    (state) => state.category.allCategories.data
  );

  // variables
  const { width } = useWindowSize();
  const maxLength = 100;
  const validateSchema = Yup.object().shape({
    title: Yup.string()
      .nullable()
      .required("Course title is required")
      .max(100, "Course title require maximum of 100 characters"),
    shortDescription: Yup.string()
      .nullable()
      .required("Short description is required")
      .max(220, "Short description require maximum of 220 characters"),
    description: Yup.string()
      .nullable()
      .required("Course description is required"),
    learn: Yup.string().nullable().required("Results of course is required"),
    requirement: Yup.string().nullable().required("Requirements is required"),
    // levelIds: Yup.array().min(1, "Course level is required"),
    // audioLanguages: Yup.array().min(1, "Audio language is required"),
    // closeCaptionIds: Yup.array().min(1, "Close caption is required"),
    categoryId: Yup.string().required("Course category is required"),
  });
  const initialValues = {
    id: "",
    title: "",
    shortDescription: "",
    description: "",
    learn: "",
    requirement: "",
    levelIds: [],
    audioLanguageIds: [],
    closeCaptionIds: [],
    categoryId: "",
  };

  const {
    id,
    title,
    shortDescription,
    description,
    learn,
    requirement,
    levelIds,
    audioLanguageIds,
    closeCaptionIds,
    categoryId,
  } = editingCourse;

  const vals = {
    id,
    title,
    shortDescription,
    description,
    learn,
    requirement,
    levelIds,
    audioLanguageIds,
    closeCaptionIds,
    categoryId,
  };
  // logics before hooks

  // hooks
  const dispatch = useDispatch();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    setValues,
    values,
    errors,
    touched,
    dirty,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: validateSchema,
    onSubmit: () => {
      handleSaveChange();
      setStep(step + 1);
    },
  });
  // logics

  const handleSaveChange = async () => {
    await dispatch(editingCourseActions.changeBasicInfo(values));
    dispatch(updateCourseAction());
  };
  // UI partials

  // effects
  useEffect(() => {
    if (isUpdate) {
      setValues(vals);
    }
  }, [editingCourse]);

  useEffect(() => {
    if (id) {
      setFieldValue("id", id);
    }
  }, [createCourse]);

  useEffect(() => {
    dispatch(getAllCategoriesAction());
    dispatch(getAllCourseLevelAction());
    dispatch(getAllAudioLanguageAction());
  }, []);

  useEffect(() => {
    if (dirty && !isUpdate && !id) {
      dispatch(createCourseAction());
    }
  }, [dirty]);

  // render
  return (
    <div className={clsx(styles.content)}>
      <TabTitle
        setAutoSave={setAutoSave}
        isAutoSave={autoSave}
        onSave={() => {
          if (id) {
            handleSaveChange();
          } else {
            toast.info("You have nothing to save. Please fill the form first");
          }
        }}
        isProcessing={updateCourse?.processing}
      >
        <i className="bi bi-info-circle mr-2"></i>Basic information
      </TabTitle>

      <div className={clsx(styles.bodyContent, "position-relative")}>
        <Form
          noValidate
          id={"basicTab"}
          // onSubmit={handleSubmit}
          className={clsx(styles.courseTitleColumn)}
        >
          <span className={clsx(styles.titleInput)}>Course Title*</span>
          <div className={clsx(styles.inputNotifyContainer, "mb-2")}>
            <Form.Group
              controlId="title"
              className={clsx("px-0 mb-3", styles.inputRow)}
            >
              <InputField
                name={"title"}
                type={"text"}
                placeholder="Course title here"
                value={values.title || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                touchedValue={touched.title}
                errorsValue={errors.title}
                maxLength={maxLength}
              />
              <button
                className={clsx("btn", styles.buttonNotify, "text-white")}
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                {values.title ? maxLength - values.title.length : maxLength}
              </button>
            </Form.Group>
          </div>
          <span className={clsx(styles.titleInput, "mt-2")}>
            Short Description*
          </span>
          <div className={clsx(styles.inputNotifyContainer)}>
            <Form.Group
              className="px-0 mb-3 w-100"
              controlId="shortDescription"
            >
              <TextArea
                type={"textarea"}
                placeholder={"Item description here..."}
                rows={3}
                value={values.shortDescription || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="off"
                touchedValue={touched.shortDescription}
                errorsValue={errors.shortDescription}
                note={"220 words"}
              />
            </Form.Group>
          </div>
          <span className={clsx(styles.titleInput)}>Course Description*</span>
          <div className={clsx(styles.inputNotifyContainer)}>
            <Form.Group className="px-0 mb-3 w-100" controlId="description">
              <RichText
                name={"description"}
                value={values.description || ""}
                onChange={(val) => setFieldValue("description", val)}
                placeholder={"Insert your course description"}
                error={"Course description is required"}
                // error={errors.description}
                setFieldError={() =>
                  setFieldError("description", "Course description is required")
                }
                touched={touched.description}
                setFieldTouched={() => setFieldTouched("description", true)}
              />
            </Form.Group>
          </div>
          <div
            className={clsx(styles.courseContainerRow, {
              [styles.courseContainerColumn]: width < 768,
              ["mt-2"]: width < 768,
              ["mt-4"]: width >= 768,
            })}
          >
            <div
              className={clsx(styles.courseTitle, {
                [styles.courseTitleRow]: width >= 768,
                ["mt-4"]: width < 768,
              })}
            >
              <span className={clsx(styles.titleInput)}>
                What will students learn in your course?*
              </span>
              <div className={clsx(styles.inputNotifyContainer)}>
                <Form.Group className="px-0 mb-3 w-100" controlId="learn">
                  <TextArea
                    type={"textarea"}
                    rows={3}
                    value={values.learn || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    touchedValue={touched.learn}
                    errorsValue={errors.learn}
                    note={
                      "Student will gain this skills, knowledge after completing this course. (One per line)."
                    }
                  />
                </Form.Group>
              </div>
            </div>
            <div
              className={clsx(styles.courseTitle, {
                [styles.courseTitleRow]: width >= 768,
              })}
            >
              <span className={clsx(styles.titleInput)}>Requirements*</span>
              <div className={clsx(styles.inputNotifyContainer)}>
                <Form.Group className="px-0 mb-3 w-100" controlId="requirement">
                  <TextArea
                    type={"textarea"}
                    rows={3}
                    value={values.requirement}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    touchedValue={touched.requirement}
                    errorsValue={errors.requirement}
                    note={
                      "What knowledge, technology, tools required by users to start this course. (One per line)."
                    }
                  />
                </Form.Group>
              </div>
            </div>
          </div>
          <div
            className={clsx(styles.courseContainerRow, "mt-2", {
              [styles.courseContainerColumn]: width < 768,
              // ["mt-2"]: width < 768,
              // ["mt-4"]: width >= 768,
            })}
          >
            <div
              className={clsx(styles.courseTitle, {
                [styles.courseTitleRow]: width >= 768,
              })}
            >
              <span className={clsx(styles.titleInput)}>Course Level</span>
              <div className={`${styles.inputField}`}>
                <Form.Group className="px-0 mb-3 w-100" controlId="levelIds">
                  <SelectField
                    name={"levelIds"}
                    value={values.levelIds || []}
                    onChange={(val) => setFieldValue("levelIds", val)}
                    listValue={CourseLevel}
                    isLoading={false}
                    placeholder={"Select Level"}
                    error={errors.levelIds}
                    setFieldError={() =>
                      setFieldError("levelIds", "Course level is required")
                    }
                    touched={touched.levelIds}
                    setFieldTouched={() => {
                      setFieldTouched("levelIds", true);
                    }}
                    multiple={true}
                  />
                </Form.Group>
              </div>
            </div>
            <div
              className={clsx(styles.courseTitle, {
                [styles.courseTitleRow]: width >= 768,
              })}
            >
              <span className={clsx(styles.titleInput)}>Audio Language</span>
              <div className={`${styles.inputField}`}>
                <Form.Group
                  className="px-0 mb-3 w-100"
                  controlId="audioLanguageIds"
                >
                  <SelectField
                    name={"audioLanguageIds"}
                    value={values.audioLanguageIds || []}
                    onChange={(val) => setFieldValue("audioLanguageIds", val)}
                    listValue={AudioLanguage}
                    isLoading={false}
                    placeholder={"Select Audio"}
                    error={errors.audioLanguageIds}
                    setFieldError={() =>
                      setFieldError(
                        "audioLanguageIds",
                        "Audio language is required"
                      )
                    }
                    touched={touched.audioLanguageIds}
                    setFieldTouched={() => {
                      setFieldTouched("audioLanguageIds", true);
                    }}
                    multiple={true}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
          <div
            className={clsx(styles.courseContainerRow, {
              [styles.courseContainerColumn]: width < 768,
              ["mt-2"]: width < 768,
              ["mt-4"]: width >= 768,
            })}
          >
            <div
              className={clsx(styles.courseTitle, {
                [styles.courseTitleRow]: width >= 768,
              })}
            >
              <span className={clsx(styles.titleInput)}>Course Category*</span>
              <div className={`${styles.inputField}`}>
                <Form.Group className="px-0 mb-3 w-100" controlId="categoryId">
                  <SelectField
                    name={"categoryId"}
                    value={values.categoryId || ""}
                    onChange={(val) => setFieldValue("categoryId", val)}
                    listValue={CourseCategory ? CourseCategory : []}
                    isLoading={false}
                    placeholder={"Select course category"}
                    error={errors.categoryId}
                    setFieldError={() =>
                      setFieldError("categoryId", "Course category is required")
                    }
                    touched={touched.categoryId}
                    setFieldTouched={() => {
                      setFieldTouched("categoryId", true);
                    }}
                    isCate={true}
                    multiple={false}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
        </Form>

        {getDetailsCourseForUpdate.processing && <SpinnerMark />}
      </div>
      <div className="d-flex justify-content-between mt-5">
        <Button
          variant="primary"
          onClick={() => {
            setStep(step - 1);
          }}
          disabled={step == 1 ? true : false}
        >
          Prev
        </Button>

        <Button variant="primary" onClick={handleSubmit}>
          Next
        </Button>
      </div>
    </div>
  );
};

/* required: display name */
BasicTab.displayName = "BasicTab";

/* required: props types */
BasicTab.propTypes = {};

/* required: default props */
BasicTab.defaultProps = {};

export { BasicTab };
